import * as React from "react";
import { useState } from "react";

import axios from "axios";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "stretch",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    padding: "2em",
  },
  mainHeading: {
    textAlign: "center",
    marginBottom: "2em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  card: {
    width: "100%",
    maxWidth: 1200,
    marginTop: "1em",
    padding: "2em",
    alignSelf: "center",
  },
  form: {
    padding: "0 1em 1em 1em",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1em",
  },
  input: {
    marginTop: "1em",
    minWidth: "300px",
  },
  actions: {
    padding: "0 1em 1em 1em",
    display: "flex",
    gap: "1em",
    justifyContent: "center",
  },
  tableContainer: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  transactionContainer: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  table: {
    minWidth: 650,
  },
  tableCell: {
    padding: "8px 16px",
    border: "1px solid #e0e0e0",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  sectionTitle: {
    marginTop: "2em",
    marginBottom: "1em",
    color: theme.palette.primary.main,
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.grey[500],
    fontStyle: "italic",
    margin: "2em 0",
  },
  buttonContainer: {
    display: "flex",
    gap: "1em",
    justifyContent: "center",
    marginTop: "1em",
  },
}));

interface VDXUserDetails {
  vdx_user_id: string;
  external_id: string;
  tracking_id: string;
  sprive_plus_status: string;
  dashboard_landed_at: string;
}

interface VDXTransaction {
  [key: string]: any;
}

interface VDXApiResponse {
  status: boolean;
  message: string;
  data: Array<{
    userDetails: VDXUserDetails[];
    transactionDetails: VDXTransaction[];
  }>;
  meta: {
    status: boolean;
    message: string;
  };
}

const VdxDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [vdxDetails, setVdxDetails] = useState<VDXUserDetails | null>(null);
  const [vdxTransactions, setVdxTransactions] = useState<VDXTransaction[]>([]);
  const [transactionHeaders, setTransactionHeaders] = useState<{ [key: string]: string }>({});
  const [hasVdxDetails, setHasVdxDetails] = useState(false);
  const [addToVdxLoading, setAddToVdxLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [editRow, setEditRow] = useState<VDXTransaction | null>(null);
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);

  // const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();

  const isSprivePlusEnabled = () =>
    vdxDetails?.sprive_plus_status === "completed" ||
    vdxDetails?.sprive_plus_status === "success";

  const isTransactionRefreshEnabled = () => {
    if (!vdxDetails) return false;
    return !!(vdxDetails.vdx_user_id && vdxDetails.external_id && vdxDetails.tracking_id);
  };

  const formatUTCDateTime = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;

    const pad = (num: number) => String(num).padStart(2, "0");

    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    const seconds = pad(date.getUTCSeconds());

    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  const getTransactionHeaderLabel = (key: string, defaultLabel: string) => {
    if (key === 'status') {
      return `${defaultLabel} (VDX Status)`;
    }
    if (key === 'transaction_status') {
      return `${defaultLabel} (Sprive Reward Status)`;
    }
    return defaultLabel;
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setLoading(true);
    setVdxDetails(null);
    setVdxTransactions([]);
    setTransactionHeaders({});
    setHasVdxDetails(false);

    try {
      const config = getConfigGet({ email: email.trim() });
      const response = await axios.get(ENDPOINTS.VDX_DASHBOARD_DETAILS, config);

      const responseData = response.data as VDXApiResponse;
      if (responseData && responseData.status && responseData.data && responseData.data.length > 0) {
        const firstData = responseData.data[0];
        if (firstData.userDetails && firstData.userDetails.length > 0) {
          setVdxDetails(firstData.userDetails[0]);
          setHasVdxDetails(true);

          // Extract header row and create mapping
          if (firstData.transactionDetails && firstData.transactionDetails.length > 0) {
            const headerRow = firstData.transactionDetails[0];
            const headerMapping: { [key: string]: string } = {};

            // Create mapping from API keys to display names
            // Map uppercase header keys to lowercase data keys
            Object.keys(headerRow).forEach(key => {
              const lowercaseKey = key.toLowerCase();
              headerMapping[lowercaseKey] = headerRow[key];
            });
            setTransactionHeaders(headerMapping);

            // Filter out the header row from transaction details
            const transactionData = firstData.transactionDetails.filter(
              (transaction, index) => index > 0
            );


            setVdxTransactions(transactionData);
          } else {
            setTransactionHeaders({});
            setVdxTransactions([]);
          }

          notify("VDX details found successfully", "info");
        } else {
          setHasVdxDetails(false);
          notify("No VDX details found for this email", "warning");
        }
      } else {
        setHasVdxDetails(false);
        notify("No VDX details found for this email", "warning");
      }
    } catch (error: any) {
      console.error("Error fetching VDX details:", error);
      notify(
        error?.response?.data?.message || "Failed to fetch VDX details",
        "error"
      );
      setHasVdxDetails(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToVdx = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setAddToVdxLoading(true);
    try {
      const config = getConfigPost();
      const params = { email: email.trim() };

      await axios.post(ENDPOINTS.VDX_DASHBOARD_DETAILS, {}, {
        headers: config,
        params: params
      });
      notify("User added to VDX successfully", "info");

      // Refresh the data after adding
      setTimeout(() => {
        handleEmailSubmit();
      }, 1000);
    } catch (error: any) {
      console.error("Error adding user to VDX:", error);
      notify(
        error?.response?.data?.message || "Failed to add user to VDX",
        "error"
      );
    } finally {
      setAddToVdxLoading(false);
    }
  };

  const handleTransactionRefresh = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setRefreshLoading(true);
    try {
      const config = getConfigPost();
      const params = { email: email.trim() };

      await axios.patch(ENDPOINTS.VDX_DASHBOARD_DETAILS, {}, {
        headers: config,
        params: params
      });
      notify("Transaction details refreshed successfully", "info");

      // Refresh the data after updating
      setTimeout(() => {
        handleEmailSubmit();
      }, 1000);
    } catch (error: any) {
      console.error("Error refreshing transactions:", error);
      notify(
        error?.response?.data?.message || "Failed to refresh transaction details",
        "error"
      );
    } finally {
      setRefreshLoading(false);
    }
  };

  const toInputValue = (value: string | null) => {
    const date = value ? new Date(value) : new Date();
    if (isNaN(date.getTime())) return "";
    const pad = (num: number) => String(num).padStart(2, "0");
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  };

  const openEditDialog = (transaction: VDXTransaction) => {
    setEditRow(transaction);
    setEditDate(toInputValue(transaction.transaction_date));
  };

  const closeEditDialog = () => {
    setEditRow(null);
    setEditDate("");
  };

  const handleReleaseDateSave = async () => {
    if (!editRow || !editDate) {
      notify("Please pick a transaction date", "error");
      return;
    }

    setSaving(true);
    try {
      const config = getConfigPost();
      await axios.put(
        ENDPOINTS.VDX_DASHBOARD_DETAILS,
        {
          reward_id: editRow.reward_id,
          transaction_date: `${editDate.replace("T", " ")}:00`,
        },
        { headers: config, params: { email: email.trim() } }
      );

      notify("Transaction date updated. The next transfer run will pay it.", "info");
      closeEditDialog();
      handleEmailSubmit();
    } catch (error: any) {
      console.error("Error updating transaction date:", error);
      notify(error?.response?.data?.message || "Failed to update transaction date", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderVdxUserDetails = () => {
    if (!hasVdxDetails || !vdxDetails) return null;

    return (
      <Paper className={classes.tableContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          VDX User Details
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                VDX User ID
              </TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                External ID
              </TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Tracking ID
              </TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Sprive Plus Status
              </TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Dashboard Landed At
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell}>
                {vdxDetails.vdx_user_id}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {vdxDetails.external_id}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {vdxDetails.tracking_id}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {vdxDetails.sprive_plus_status}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {formatUTCDateTime(vdxDetails.dashboard_landed_at)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const renderVdxTransactions = () => {
    if (!hasVdxDetails) return null;

    if (!vdxTransactions || vdxTransactions.length === 0) {
      return (
        <Paper className={classes.transactionContainer}>
          <Typography variant="h6" className={classes.sectionTitle}>
            VDX Transaction Details
          </Typography>
          <Typography className={classes.noDataMessage}>
            No transaction details found
          </Typography>
        </Paper>
      );
    }

    const transactionKeys = Object.keys(transactionHeaders);

    return (
      <Paper className={classes.transactionContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          VDX Transaction Details
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {transactionKeys.map((key) => (
                <TableCell key={key} className={`${classes.tableCell} ${classes.tableHeader}`}>
                  {getTransactionHeaderLabel(key, transactionHeaders[key])}
                </TableCell>
              ))}
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vdxTransactions.map((transaction, index) => (
              <TableRow key={index}>
                {transactionKeys.map((key) => (
                  <TableCell key={key} className={classes.tableCell}>
                    {key === 'amount' && transaction[key] ? `£${transaction[key]}` :
                      key === 'rewards_amount' && transaction[key] ? `£${transaction[key]}` :
                        key === 'is_inflight' ? (transaction[key] ? 'Yes' : 'No') :
                          (key.includes('date') || key.includes('Date')) && transaction[key] ?
                            formatUTCDateTime(transaction[key]) :
                            transaction[key]}
                  </TableCell>
                ))}
                <TableCell className={classes.tableCell}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={
                      !transaction.reward_id ||
                      transaction.transaction_status !== "approved" ||
                      !!transaction.is_inflight
                    }
                    onClick={() => openEditDialog(transaction)}
                  >
                    Edit Transaction Date
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  return (
    <div className={classes.main}>
      <Typography variant="h3" component="h1" className={classes.mainHeading}>
        VDX Dashboard
      </Typography>

      <Paper className={classes.card}>

        <div className={classes.form}>
          <TextField
            className={classes.input}
            label="Enter Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            fullWidth
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleEmailSubmit();
              }
            }}
          />

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEmailSubmit}
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Searching..." : "Search VDX Details"}
            </Button>

            <Tooltip
              title={
                isSprivePlusEnabled()
                  ? "Sprive Plus is already enabled for this user"
                  : "Enables Sprive Plus, which unlocks both VDX and Partnerize brands"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAddToVdx}
                  disabled={addToVdxLoading || !email.trim() || isSprivePlusEnabled()}
                  startIcon={addToVdxLoading ? <CircularProgress size={20} /> : null}
                >
                  {addToVdxLoading ? "Adding..." : "Add to VDX"}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        {!hasVdxDetails && email && !loading && (
          <Typography className={classes.noDataMessage}>
            No VDX details found for this email
          </Typography>
        )}

        {hasVdxDetails && (
          <div className={classes.buttonContainer}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleTransactionRefresh}
              disabled={refreshLoading || !isTransactionRefreshEnabled()}
              startIcon={refreshLoading ? <CircularProgress size={20} /> : null}
            >
              {refreshLoading ? "Refreshing..." : "Transaction Refresh"}
            </Button>
          </div>
        )}
      </Paper>

      {renderVdxUserDetails()}
      {renderVdxTransactions()}

      <Dialog open={!!editRow} onClose={closeEditDialog}>
        <DialogTitle>Edit Transaction Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {editRow?.merchant_name} &mdash; £{editRow?.rewards_amount}
          </Typography>
          <TextField
            className={classes.input}
            label="Transaction Date (UTC)"
            type="datetime-local"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" component="p">
            Setting a date in the past releases the reward on the next VDX rewards transfer
            run. The money is not moved by saving this.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleReleaseDateSave}
            color="primary"
            variant="contained"
            disabled={saving || !editDate}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default VdxDashboard;
